import { Alert, Button, Layout, Space, Tag } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';

import {
    BackToModesLink,
    CORRECT_SCORE_THRESHOLD,
    EXTENDED_TOTAL_ROUNDS,
    Header,
    KRAJ_NAMES,
    MapFrame,
    ModalMapPreview,
    ModalPhoto,
    OptionButton,
    OptionGrid,
    Page,
    Question,
    QuizFinalResult,
    QuizPanel,
    QuizPhotoPreview,
    QuizPhotoThumbnail,
    RoundResultModal,
    RoundStatus,
    SvgMap,
    calculateBlindMapScore,
    calculateDistanceKm,
    formatDistance,
    makeOptions,
    pickRandom,
    shuffleArray,
    useKrajFeatures,
} from '../../components/quiz';
import peaksData from '../../data/peaks.json';

const { Content } = Layout;

const ITEMS = peaksData.items.filter(i => Number.isFinite(i.lat) && Number.isFinite(i.lon) && i.kraj);
const ITEMS_WITH_POHORI = ITEMS.filter(i => i.pohori);
const POHORI_NAMES = Array.from(new Set(ITEMS.filter(i => i.pohori).map(i => i.pohori))).sort((a, b) =>
    a.localeCompare(b, 'cs'),
);

const QUESTION_TYPES = ['kraj', 'pohori', 'map', 'highest'];

const RankCard = styled.button`
    text-align: left;
    background: ${({ $state }) => ($state === 'correct' ? '#f6ffed' : $state === 'wrong' ? '#fff1f0' : '#ffffff')};
    border: 2px solid ${({ $state }) => ($state === 'correct' ? '#52c41a' : $state === 'wrong' ? '#ff4d4f' : '#d9d9d9')};
    border-radius: 10px;
    padding: 16px 18px;
    cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
    transition: transform 0.12s ease, border-color 0.2s ease, background 0.2s ease;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    width: 100%;

    &:hover {
        transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-1px)')};
        border-color: ${({ $state, $disabled }) => ($disabled || $state ? undefined : '#c80707')};
    }
`;

const RankCardName = styled.div`
    font-size: clamp(18px, 2.4vw, 22px);
    font-weight: 700;
`;

const RankCardMeta = styled.div`
    font-size: 13px;
    color: #595959;
    margin-top: 4px;
`;

const RankCardStack = styled.div`
    display: grid;
    gap: 10px;
`;

const RankCardContent = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
`;

const RankCardCopy = styled.div`
    min-width: 0;
`;

const Elevation = styled.span`
    font-weight: 700;
    color: #1f1f1f;
`;

const pickHighestSet = () => {
    const sorted = [...ITEMS].sort((a, b) => b.ele - a.ele);
    const n = sorted.length;
    if (n < 3) return shuffleArray(sorted.slice(0, 3));
    const top = sorted.slice(0, Math.max(1, Math.floor(n * 0.25)));
    const mid = sorted.slice(Math.floor(n * 0.25), Math.floor(n * 0.6));
    const low = sorted.slice(Math.floor(n * 0.6));
    const a = pickRandom(top);
    // Prevent duplicates if a band is empty and we fall back to the full list.
    const bPool = mid.length ? mid : sorted.filter(x => x !== a);
    let b = pickRandom(bPool);
    const cPool = low.length ? low : sorted.filter(x => x !== a && x !== b);
    let c = pickRandom(cPool);
    if (a.ele - b.ele < 30 && mid.length > 1) b = pickRandom(mid);
    if (b.ele - c.ele < 30 && low.length > 1) c = pickRandom(low);
    return shuffleArray([a, b, c]);
};

const makeQuestion = () => {
    const candidates = shuffleArray(QUESTION_TYPES);
    for (const type of candidates) {
        if (type === 'pohori' && ITEMS_WITH_POHORI.length === 0) continue;

        if (type === 'kraj') {
            const item = pickRandom(ITEMS);
            return { type, item, options: makeOptions(item.kraj, KRAJ_NAMES) };
        }
        if (type === 'pohori') {
            const item = pickRandom(ITEMS_WITH_POHORI);
            return { type, item, options: makeOptions(item.pohori, POHORI_NAMES) };
        }
        if (type === 'map') {
            const item = pickRandom(ITEMS);
            return { type, item, target: { lat: item.lat, lon: item.lon } };
        }
        if (type === 'highest') {
            const peaks = pickHighestSet();
            let winnerIdx = 0;
            for (let i = 1; i < peaks.length; i++) {
                if (peaks[i].ele > peaks[winnerIdx].ele) winnerIdx = i;
            }
            return { type, peaks, winnerIdx };
        }
    }
    return null;
};

const PeaksGame = () => {
    const { features, error } = useKrajFeatures();
    const [round, setRound] = useState(1);
    const [score, setScore] = useState(0);
    const [question, setQuestion] = useState(makeQuestion);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [guessPoint, setGuessPoint] = useState(null);
    const [mapResult, setMapResult] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [rounds, setRounds] = useState([]);
    const [finished, setFinished] = useState(false);

    const maxScore = EXTENDED_TOTAL_ROUNDS * 100;

    const restart = () => {
        setRound(1);
        setScore(0);
        setQuestion(makeQuestion());
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
        setModalVisible(false);
        setRounds([]);
        setFinished(false);
    };

    if (finished || !question) {
        return (
            <QuizFinalResult score={score} totalRounds={EXTENDED_TOTAL_ROUNDS} rounds={rounds} onRestart={restart} />
        );
    }

    const winnerPeak = question.type === 'highest' ? question.peaks[question.winnerIdx] : null;
    const correctAnswer =
        question.type === 'kraj' ? question.item.kraj : question.type === 'pohori' ? question.item.pohori : null;

    const labelForRound = () => {
        if (question.type === 'highest') return `Nejvyšší: ${winnerPeak.name}`;
        return question.item ? `${question.item.name} (${question.item.ele} m)` : '';
    };

    const answerOption = option => {
        if (selectedAnswer) return;
        setSelectedAnswer(option);
        const correct = option === correctAnswer;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: labelForRound() }]);
        setModalVisible(true);
    };

    const pickHighestPeak = peakIdx => {
        if (selectedAnswer) return;
        setSelectedAnswer(String(peakIdx));
        const correct = peakIdx === question.winnerIdx;
        if (correct) setScore(s => s + 100);
        setRounds(prev => [...prev, { correct, score: correct ? 100 : 0, label: labelForRound() }]);
        setModalVisible(true);
    };

    const submitMapGuess = () => {
        if (!guessPoint || mapResult) return;
        const distance = calculateDistanceKm(guessPoint, question.target);
        const roundScore = calculateBlindMapScore(distance);
        setMapResult({ distance, score: roundScore });
        setScore(s => s + roundScore);
        setRounds(prev => [
            ...prev,
            { correct: roundScore >= CORRECT_SCORE_THRESHOLD, score: roundScore, label: labelForRound() },
        ]);
        setModalVisible(true);
    };

    const advanceRound = () => {
        setModalVisible(false);
        setRound(r => r + 1);
        setQuestion(makeQuestion());
        setSelectedAnswer(null);
        setGuessPoint(null);
        setMapResult(null);
    };

    const showFinal = () => {
        setModalVisible(false);
        setFinished(true);
    };

    const correctForModal =
        question.type === 'highest'
            ? selectedAnswer === String(question.winnerIdx)
            : question.type === 'map' && mapResult
            ? mapResult.score >= CORRECT_SCORE_THRESHOLD
            : selectedAnswer === correctAnswer;

    const wrongVerdictForModal = (() => {
        if (question.type === 'highest' && selectedAnswer && !correctForModal) {
            const picked = question.peaks[Number(selectedAnswer)];
            return `${picked.name} není nejvyšší :(`;
        }
        if (question.type === 'map' && mapResult) {
            return `${question.item.name} jsi netrefil :(`;
        }
        if (selectedAnswer && selectedAnswer !== correctAnswer) {
            return `${selectedAnswer} není správná odpověď :(`;
        }
        return null;
    })();

    if (question.type === 'highest') {
        return (
            <>
                <QuizPanel>
                    <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={EXTENDED_TOTAL_ROUNDS} />
                    <Question>
                        <p>Hory a vrcholy ČR</p>
                        <h2>Který z těchto vrcholů je nejvyšší?</h2>
                        <p>Vyber ten s nejvyšší nadmořskou výškou.</p>
                    </Question>
                    <RankCardStack>
                        {question.peaks.map((peak, idx) => {
                            const idxStr = String(idx);
                            const state = !selectedAnswer
                                ? null
                                : idx === question.winnerIdx
                                ? 'correct'
                                : selectedAnswer === idxStr
                                ? 'wrong'
                                : null;
                            return (
                                <RankCard
                                    key={`${idx}-${peak.name}-${peak.lat}-${peak.lon}`}
                                    type="button"
                                    $state={state}
                                    $disabled={!!selectedAnswer}
                                    onClick={() => pickHighestPeak(idx)}
                                >
                                    <RankCardContent>
                                        <QuizPhotoThumbnail item={peak} alt={peak.name} />
                                        <RankCardCopy>
                                            <RankCardName>{peak.name}</RankCardName>
                                            <RankCardMeta>
                                                {peak.kraj}
                                                {peak.pohori ? ` • ${peak.pohori}` : ''}
                                                {selectedAnswer ? (
                                                    <>
                                                        {' '}
                                                        • <Elevation>{peak.ele} m n. m.</Elevation>
                                                    </>
                                                ) : (
                                                    ''
                                                )}
                                            </RankCardMeta>
                                        </RankCardCopy>
                                    </RankCardContent>
                                </RankCard>
                            );
                        })}
                    </RankCardStack>
                </QuizPanel>
                <RoundResultModal
                    visible={modalVisible}
                    correct={correctForModal}
                    round={round}
                    totalRounds={EXTENDED_TOTAL_ROUNDS}
                    onNext={advanceRound}
                    onShowResult={showFinal}
                    wrongVerdict={wrongVerdictForModal}
                >
                    <h3>Správná odpověď</h3>
                    <ModalPhoto item={winnerPeak} alt={winnerPeak.name} />
                    <p>
                        Nejvyšší byl <strong style={{ fontSize: '1.2em' }}>{winnerPeak.name}</strong> ({winnerPeak.ele}{' '}
                        m n. m.).
                    </p>
                    <p>
                        {winnerPeak.kraj}
                        {winnerPeak.pohori ? ` • ${winnerPeak.pohori}` : ''}
                    </p>
                </RoundResultModal>
            </>
        );
    }

    const item = question.item;

    return (
        <>
            <QuizPanel>
                <RoundStatus round={round} score={score} maxScore={maxScore} totalRounds={EXTENDED_TOTAL_ROUNDS} />

                {question.type === 'kraj' && (
                    <>
                        <Question>
                            <p>Vrchol v Česku ({item.ele} m n. m.)</p>
                            <h2>{item.name}</h2>
                            <p>Ve kterém kraji se nachází?</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.name} label="Fotka vrcholu" />
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswer={correctAnswer}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerOption(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                )}

                {question.type === 'pohori' && (
                    <>
                        <Question>
                            <p>Vrchol v Česku ({item.ele} m n. m.)</p>
                            <h2>{item.name}</h2>
                            <p>Ve kterém pohoří leží?</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.name} label="Fotka vrcholu" />
                        <OptionGrid>
                            {question.options.map(option => (
                                <OptionButton
                                    key={option}
                                    answer={option}
                                    correctAnswer={correctAnswer}
                                    disabled={!!selectedAnswer}
                                    selectedAnswer={selectedAnswer}
                                    onClick={() => answerOption(option)}
                                />
                            ))}
                        </OptionGrid>
                    </>
                )}

                {question.type === 'map' && (
                    <>
                        <Question>
                            <p>Slepá mapa vrcholů ({item.ele} m n. m.)</p>
                            <h2>{item.name}</h2>
                            <p>Klikni do mapy přibližně tam, kde vrchol leží.</p>
                        </Question>
                        <QuizPhotoPreview item={item} alt={item.name} label="Fotka vrcholu" />
                        {error && (
                            <Alert type="error" message="Mapa se nepodařila načíst" description={error.message} />
                        )}
                        <MapFrame>
                            {features ? (
                                <SvgMap
                                    features={features}
                                    guessPoint={guessPoint}
                                    targetPoint={mapResult ? question.target : null}
                                    onMapClick={mapResult ? null : setGuessPoint}
                                />
                            ) : (
                                <Alert type="info" message="Načítám mapu..." />
                            )}
                        </MapFrame>
                        {!mapResult && (
                            <Space wrap>
                                <Button type="primary" disabled={!guessPoint} onClick={submitMapGuess}>
                                    Vyhodnotit
                                </Button>
                                {guessPoint && <Tag>Tip umístěn</Tag>}
                            </Space>
                        )}
                    </>
                )}
            </QuizPanel>

            <RoundResultModal
                visible={modalVisible}
                correct={correctForModal}
                round={round}
                totalRounds={EXTENDED_TOTAL_ROUNDS}
                onNext={advanceRound}
                onShowResult={showFinal}
                wrongVerdict={wrongVerdictForModal}
            >
                <h3>Správná odpověď</h3>
                <ModalPhoto item={item} alt={item.name} />
                <p>
                    <strong style={{ fontSize: '1.2em' }}>{item.name}</strong>
                    <br />
                    {item.ele} m n. m. • {item.kraj}
                    {item.pohori ? ` • ${item.pohori}` : ''}
                </p>
                {question.type === 'map' && mapResult && (
                    <>
                        <p>
                            Tvůj tip byl <strong>{formatDistance(mapResult.distance)}</strong> od vrcholu ·{' '}
                            <strong>{mapResult.score}</strong> bodů za kolo.
                        </p>
                        <ModalMapPreview features={features} guessPoint={guessPoint} targetPoint={question.target} />
                    </>
                )}
                {question.type !== 'map' && selectedAnswer && selectedAnswer !== correctAnswer && (
                    <p>
                        Tvůj tip byl: <strong>{selectedAnswer}</strong>.
                    </p>
                )}
            </RoundResultModal>
        </>
    );
};

export const Peaks = () => (
    <Content>
        <Page>
            <Header>
                <BackToModesLink />
                <h1>Hory a vrcholy</h1>
                <p>
                    Přes 1300 pojmenovaných vrcholů v Česku nad 800 m n. m. Pět kol s mixem otázek — kraj, pohoří, slepá
                    mapa, „který je nejvyšší“. Data: OpenStreetMap (ODbL) + Wikipedie (seznam tisícovek).
                </p>
            </Header>
            <PeaksGame />
        </Page>
    </Content>
);
